import { FC, useState, MouseEvent, useCallback } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { styled } from '@robocorp/theme';
import { Box, Button, Dialog, Input, Typography } from '@robocorp/components';
import { ConfirmProps } from './Confirm';

const Strong = styled('strong')`
  font-weight: 700;
`;

interface Props extends Omit<ConfirmProps, 'confirmButtonText'> {
  confirmPhrase: string;
}

const ConfirmWithPhrase: FC<Props> = ({ children, title, text, confirmPhrase }) => {
  const { t } = useTranslation();

  const [open, setOpen] = useState<boolean>(false);
  const [callback, setCallback] = useState<(() => void) | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { isValid },
  } = useForm({
    resolver: zodResolver(z.object({ confirmInput: z.literal(confirmPhrase) })),
    mode: 'onChange',
    defaultValues: {
      confirmInput: '',
    },
  });

  const onOpen = useCallback(
    (cb: (event: MouseEvent) => void) => (event: MouseEvent) => {
      event.preventDefault();
      reset({ confirmInput: '' });

      setOpen(true);
      setCallback(() => () => cb(event));
    },
    [reset],
  );

  const onClose = () => setOpen(false);

  const onConfirm = () => {
    if (callback) {
      callback();
    }
    onClose();
  };

  return (
    <>
      {children(onOpen)}
      <Dialog open={open} onClose={onClose}>
        <form onSubmit={handleSubmit(onConfirm)} autoComplete="off">
          <Dialog.Header>
            <Dialog.Header.Title title={title} />
            <Dialog.Header.Description>{text}</Dialog.Header.Description>
          </Dialog.Header>
          <Dialog.Content>
            <Typography>
              <Trans
                i18nKey="hardConfirmExplain"
                values={{ confirmPhrase }}
                components={{ strong: <Strong /> }}
              />
            </Typography>
            <Box py={8}>
              <Input {...register('confirmInput')} aria-label="confirm" autoFocus />
            </Box>
          </Dialog.Content>
          <Dialog.Actions>
            <Button type="submit" disabled={!isValid} data-testid="dialog__confirm-submit">
              {t('confirm')}
            </Button>
            <Button onClick={onClose} variant="secondary">
              {t('cancel')}
            </Button>
          </Dialog.Actions>
        </form>
      </Dialog>
    </>
  );
};

export default ConfirmWithPhrase;
